import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Form } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import toast from "react-hot-toast";
import { useAuth } from "@/_zustand/auth";
import { useUpdateUser } from "@/hooks/useHome";
import { useRefrescarSesion } from "@/hooks/useAuth";
import { formatRut, cleanRut, validateRut } from "@/lib";
import { fusionarUsuarioActualizado } from "@/lib/usuarioPerfil";

export const ProfileForm = () => {
  const { user, setSesionAuth } = useAuth();
  const refrescarSesion = useRefrescarSesion();
  const [countryName, setCountryName] = useState("Chile");

  const updateUser = useUpdateUser();
  const defaultValues = {
    name: user.usuario_nombre,
    lastname: user.usuario_apellido,
    rut: formatRut(user.usuario_rut),
    email: user.usuario_correo,
    whatsapp: user.usuario_telefono,
    nombreComercial: "",
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({ defaultValues });
  //aqui
  const getFormErrorMessage = (name) =>
    errors[name] && (
      <small className="text-danger">{errors[name].message}</small>
    );

  const onSubmit = (formData) => {
    // Evitar duplicar el '+': PhoneInput retorna sin '+', pero si el valor
    // inicial del store ya tenía '+', el campo no dispara onChange y conserva el '+'.
    const telefono = formData.whatsapp
      ? formData.whatsapp.startsWith('+')
        ? formData.whatsapp
        : '+' + formData.whatsapp
      : '';

    const rutNuevo = cleanRut(formData.rut);
    // CA-36: si el rut cambia, la claim `rut` del access token vigente queda
    // desactualizada respecto del backend — hay que renovar la sesion antes
    // de dar el cambio por completo (nunca dejar claim vieja + header nuevo,
    // o viceversa, ver CA-6 / ApiCore._headers/rutDeSesion).
    // Se comparan solo dígitos + K: el backend puede guardar el rut sin guion
    // ("111111111") y el formulario lo devuelve formateado ("11111111-1"), lo
    // que daba un falso "rut cambió" y una renovación de sesión innecesaria.
    const soloDigitosRut = (r) => String(r || '').replace(/[^0-9kK]/g, '').toUpperCase();
    const rutCambio = soloDigitosRut(rutNuevo) !== soloDigitosRut(user.usuario_rut);

    let dataSend = {
      usuario_id: user.usuario_id,
      usuario_nombre: formData.name,
      usuario_apellido: formData.lastname,
      usuario_correo: user.usuario_correo,
      usuario_telefono: telefono,
      usuario_rut: rutNuevo,
      usuario_country: countryName,
    };

    updateUser.mutateAsync(dataSend)
    .then((resp) => {
      // `modificarDatosUsuario` devuelve el usuario con otros nombres de campo
      // que `login` (nombre/apellido/correo/telefono/rut/country en vez de
      // usuario_*), y `setSesionAuth` REEMPLAZA el objeto usuario completo. Sin
      // normalizar + fusionar, todos los `user.usuario_*` quedaban en
      // undefined y "Datos personales" se veía en blanco tras guardar.
      // Si la respuesta no trae `user` no hay nada que fusionar: se conserva
      // el perfil previo (fusionar el envelope metería `status` en el usuario).
      const traeUsuario =
        resp && typeof resp.user === "object" && resp.user !== null &&
        Object.keys(resp.user).length > 0;
      const updatedUser = traeUsuario ? fusionarUsuarioActualizado(user, resp) : null;
      if (!traeUsuario) {
        console.error("[ProfileForm] updateUser: user vacio en respuesta exitosa", resp);
      }

      if (!rutCambio) {
        toast.success("Se ha actualizado exitosamente");
        if (updatedUser) setSesionAuth(updatedUser);
        return;
      }

      refrescarSesion.mutateAsync()
        .then((huboSesion) => {
          if (!huboSesion) {
            toast.error("No pudimos renovar tu sesión tras el cambio de rut. Vuelve a iniciar sesión.");
            return;
          }
          toast.success("Se ha actualizado exitosamente");
          if (updatedUser) setSesionAuth(updatedUser);
        })
        .catch(() => {
          toast.error("No pudimos renovar tu sesión tras el cambio de rut. Vuelve a iniciar sesión.");
        });
    })
    .catch(() => {
      toast.error("Ha ocurrido un error al actualizar su información");
    });
  };

  return (
    <div className="profile-form-section">
      <h2 className="profile-form-title">Datos personales</h2>
     
      <Form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-4 about-us-pb">
            <label htmlFor="name" className="mb-2 about-us-textcolor">
              Nombres
            </label>
            <Controller
              name="name"
              control={control}
              rules={{ required: "Debe indicar el nombre" }}
              render={({ field, fieldState }) => (
                <Form.Group className="position-relative mb-2 ">
                  <Form.Control
                    type="text"
                    placeholder={user.usuario_nombre}
                    id={field.name}
                    {...field}
                    isInvalid={fieldState.invalid}
                  />
                  {getFormErrorMessage("name")}
                </Form.Group>
              )}
            />
          </div>
          <div className="col-4 about-us-pb">
            <label className="mb-2 about-us-textcolor">Apellidos</label>
            <Controller
              name="lastname"
              control={control}
              rules={{ required: "Debe indicar el apellido" }}
              render={({ field, fieldState }) => (
                <Form.Group className="position-relative mb-2 ">
                  <Form.Control
                    type="text"
                    placeholder={user.usuario_apellido}
                    id={field.name}
                    {...field}
                    isInvalid={fieldState.invalid}
                  />
                  {getFormErrorMessage("lastName")}
                </Form.Group>
              )}
            />
          </div>
          <div className="col-4 about-us-pb">
            <label className="mb-2 about-us-textcolor">
              Número de identificación
            </label>
            <Controller
              name="rut"
              control={control}
              rules={{
                required: "Debe indicar el rut",
                validate: (value) => {
                  if (!value) return "Debe indicar el rut";
                  // Permite formato con o sin puntos, pero REQUIERE guion antes del dígito verificador
                  const rutLimpio = value.replace(/[.\s]/g, '');
                  if (!/^[0-9]+-[0-9kK]{1}$/.test(rutLimpio)) {
                    return "Rut no válido (formato: 12345678-9)";
                  }
                  // Validar dígito verificador con algoritmo módulo 11
                  if (!validateRut(value)) {
                    return "Rut no válido (dígito verificador incorrecto)";
                  }
                  return true;
                }
              }}
              render={({ field, fieldState }) => (
                <Form.Group className="position-relative mb-2 ">
                  <Form.Control
                    type="text"
                    id={field.name}
                    {...field}
                    isInvalid={fieldState.invalid}
                  />
                  {getFormErrorMessage("rut")}
                </Form.Group>
              )}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-6 about-us-pb">
            <label className="mb-2 about-us-textcolor">
              Teléfono (whatsapp)
            </label>
            {/* <Controller name="whatsapp" control={control}
                      rules={{ required: "Debe indicar el whatsapp" }}
                      render={({ field, fieldState }) => (
                        <Form.Group className="position-relative mb-2 ">
                          <Form.Control type="number" placeholder="whatsapp" 
                          id={field.name} {...field} isInvalid={fieldState.invalid} />
                          {getFormErrorMessage("whatsapp")}
                        </Form.Group>
                      )}
                    /> */}
            <Controller
              name="whatsapp"
              control={control}
              defaultValue={user.usuario_telefono}
              render={({ field }) => (
                <PhoneInput
                  {...field}
                  country={"cl"}
                  enableSearch={true}
                  countryCodeEditable={true}
                  inputStyle={{ width: "100%" }}
                  // ⭐️ CLAVE: Interceptar el onChange para obtener el objeto 'data'
                  onChange={(value, data) => {
                    // 1. El objeto 'data' contiene la información de la bandera seleccionada.
                    const countryName = data.name;

                    // 2. Actualiza el estado con el nombre del país (ej: "Chile").
                    setCountryName(countryName);

                    // 3. 🔑 CRUCIAL: Llama al onChange de React Hook Form para que guarde el valor del teléfono.
                    field.onChange(value);

                  }}
                />
              )}
            />
          </div>

          <div className="col-6 about-us-pb">
            <label className="mb-2 about-us-textcolor">Correo</label>
            <Controller
              name="email"
              control={control}
              disabled={true}
              rules={{
                required: "Debe indicar el correo",
                pattern: {
                  value: /^[A-Z0-9.%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}$/i,
                  message: "Correo no válido",
                },
              }}
              render={({ field, fieldState }) => (
                <Form.Group className="position-relative mb-2 ">
                  <Form.Control
                    type="email"
                    placeholder={user.usuario_correo}
                    id={field.email}
                    {...field}
                    isInvalid={fieldState.invalid}
                  />
                  {getFormErrorMessage("email")}
                </Form.Group>
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-6 about-us-pb"></div>

          <div className="col-6 about-us-pb" style={{ textAlign: "right" }}>
            <button
              className="btn rounded-6 montserrat-bold text-white"
              type="submit"
              onClick={handleSubmit}
              style={{
                backgroundColor: "#8DC200",
                width: "60%",
                borderRadius: "20px",
              }}
            >
              Actualizar datos
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
};
